import React, { FC, useEffect } from "react";
import moment from "moment";
import RepoCompleted from "../../../../assets/RepoCompleted.svg";
import "./style.scss";
import { useStore } from "zustand";
import { dashboardStore } from "entities/dashboard";
import { getCurrencySymbol, openAssignmentDocument } from "helpers/functions";
import { useTranslation } from "react-i18next";
import "moment/locale/ru";
import "moment/locale/hy-am";
import { IDepositPayment, IRepoDeal } from "entities/account";
import { useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";

type MainPropsType = {
  label: string;
  text: string;
};
const RepoFieldMainComponent: FC<MainPropsType> = ({ label, text }) => {
  return (
    <div className="repo-amount-reward-field">
      <p>{label}</p>
      <span>{text}</span>
    </div>
  );
};

export const generateDateYieldMap = (
  startDate: string,
  endDate: string,
  annualYield: number,
  amount: number
) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const now = moment();

  const result: {
    year: number;
    totalDaysInYear: number;
    includedDaysCount: number;
    includedDaysProfit: number;
    dailyProfit: number;
    passed: boolean;
    months: {
      name: string;
      daysIncluded: number;
      profit: number;
      dateRangeLabel: string;
      passed: boolean;
    }[];
  }[] = [];

  let current = start.clone();
  const dayOfMonth = start.date();

  while (current.isBefore(end)) {
    const next = current.clone().add(1, "month").date(dayOfMonth);
    const periodStart = current.clone();
    const periodEnd = moment.min(next, end.clone());

    const daysInRange = periodEnd.diff(periodStart, "days");
    const year = periodStart.year();
    const monthName = periodStart.format("MMM");

    let yearEntry = result.find((entry) => entry.year === year);

    if (!yearEntry) {
      const isLeapYear = moment(`${year}-01-01`).isLeapYear();
      const totalDaysInYear = isLeapYear ? 366 : 365;
      const dailyProfit = (annualYield / 100) * (1 / totalDaysInYear) * amount;

      yearEntry = {
        year,
        totalDaysInYear,
        includedDaysCount: 0,
        includedDaysProfit: 0,
        dailyProfit,
        passed: false,
        months: [],
      };

      result.push(yearEntry);
    }

    const monthValue = daysInRange * yearEntry.dailyProfit;
    const dateRangeLabel = `${periodStart.format("D MMM")} - ${periodEnd.format(
      "D MMM"
    )}`;
    const passed = now.isAfter(periodEnd, "day");

    yearEntry.includedDaysCount += daysInRange;
    yearEntry.includedDaysProfit =
      yearEntry.includedDaysCount * yearEntry.dailyProfit;

    yearEntry.months.push({
      name: monthName,
      daysIncluded: daysInRange,
      profit: monthValue,
      dateRangeLabel,
      passed,
    });

    current = next;
  }

  result.forEach((yearEntry) => {
    yearEntry.passed = yearEntry.months.every((m) => m.passed);
  });

  return result;
};

// todo test/rewrite
const getTimePassedPercent = (startDate: string, endDate: string) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const today = moment();

  const fullDuration = end.diff(start, "days");
  const passedDuration = today.isAfter(end)
    ? fullDuration
    : today.diff(start, "days");

  const timePassedPercent = Math.min(
    (passedDuration / fullDuration) * 100,
    100
  );

  return parseFloat(timePassedPercent.toFixed(2));
};

type FooterPropsType = {
  data: any;
  symbol: string;
};

const RepoFieldFooterComponent: FC<FooterPropsType> = ({ data, symbol }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("app");
  moment.locale(i18n.language == "am" ? "hy-am" : i18n.language);

  const dateYieldMap = generateDateYieldMap(
    data.start_date,
    data.end_date,
    data.percent,
    data.fix_amount
  );

  const timePassedPercent = getTimePassedPercent(
    data.start_date,
    data.end_date
  );
  const currentYear = moment().year();
  const currentYearEntry = dateYieldMap.find(
    (entry) => entry.year === currentYear
  );
  let firstMonthLabel = "";
  let lastMonthLabel = "";
  if (currentYearEntry) {
    const firstMonth = currentYearEntry.months[0];
    const lastMonth =
      currentYearEntry.months[currentYearEntry.months.length - 1];
    firstMonthLabel = `${firstMonth.name} ${currentYear}`;
    lastMonthLabel = `${lastMonth.name} ${currentYear}`;
  }

  const generateProgressBar = () => {
    if (data.payment_type == "one_time") {
      return (
        <div
          className={
            !data.paid
              ? "progress-blocks-wrapper progress-block-wrapper-load"
              : "progress-blocks-wrapper progress-block-wrapper-load progress-blocks-completed-wrapper"
          }
        >
          <div
            style={{ width: `${timePassedPercent}%` }}
            className="progress-block"
          ></div>
        </div>
      );
    } else if (data.payment_type == "yearly") {
      return (
        <div
          className={
            !data.paid
              ? "progress-blocks-wrapper"
              : "progress-blocks-wrapper progress-blocks-completed-wrapper"
          }
        >
          {dateYieldMap.map((entry) => (
            <div
              key={entry.year}
              style={{
                backgroundColor: entry.passed ? "#f8009e" : "#efefef",
              }}
              className="progress-block"
            >
              <div className="progress-block-hover">
                <p>
                  {entry.passed && <img src={RepoCompleted} alt="Completed" />}
                  {entry.year} -
                </p>
                {+entry.includedDaysProfit.toFixed(2)} {symbol}
              </div>
            </div>
          ))}
        </div>
      );
    } else if (data.payment_type == "monthly") {
      return (
        <>
          <div
            className={
              !data.paid
                ? "progress-blocks-wrapper"
                : "progress-blocks-wrapper progress-blocks-completed-wrapper"
            }
          >
            {dateYieldMap
              .filter((entry) => entry.year === new Date().getFullYear())
              .flatMap((entry) =>
                entry.months.map((monthName, idx) => (
                  <div
                    key={`${entry.year}-${monthName}-${idx}`}
                    style={{
                      backgroundColor: monthName.passed ? "#f8009e" : "#efefef",
                    }}
                    className={
                      data.paid
                        ? "progress-block progress-completed"
                        : "progress-block"
                    }
                  >
                    <div className="progress-block-hover">
                      <p>
                        {monthName.passed && (
                          <img src={RepoCompleted} alt="Completed" />
                        )}
                        {monthName.name} -
                      </p>
                      {+monthName.profit.toFixed(2)} {symbol}
                    </div>
                  </div>
                ))
              )}
          </div>

          {dateYieldMap.length > 1 && (
            <div className="progress-date-blocks">
              <p style={{ width: "auto" }}>{firstMonthLabel}</p>
              <p style={{ width: "auto" }}>{lastMonthLabel}</p>
            </div>
          )}

          {dateYieldMap.length > 1 && (
            <div
              style={{ marginTop: "8px" }}
              className={
                !data.paid
                  ? "progress-blocks-wrapper"
                  : "progress-blocks-wrapper progress-blocks-completed-wrapper"
              }
            >
              {dateYieldMap.map((entry) => (
                <div key={entry.year} className="progress-block">
                  <div className="progress-block-hover">
                    {data.paid && <img src={RepoCompleted} alt="Completed" />}
                    {entry.year} - {+entry.includedDaysProfit.toFixed(2)}{" "}
                    {symbol}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      );
    } else {
      return (
        <div
          className={
            !data.paid
              ? "progress-blocks-wrapper progress-block-wrapper-load"
              : "progress-blocks-wrapper progress-block-wrapper-load progress-blocks-completed-wrapper"
          }
        >
          <div
            style={{ width: `${timePassedPercent}%` }}
            className="progress-block"
          ></div>
        </div>
      );
    }
  };

  const assignment = data.assignment;
  const signed = assignment?.signed;

  return (
    <div className="repo-transaction-field-footer">
      {generateProgressBar()}

      <div className="progress-date-blocks">
        <p style={{ width: "auto" }}>
          {moment(data.start_date).format("DD MMM YYYY")}
        </p>
        {assignment && (
          <p
            onClick={() => {
              if (signed) {
                // В РЕПО и депозитах поручение приходит без ссылки на файл —
                // короткую берём по нажатию.
                openAssignmentDocument(assignment.id);
              } else {
                navigate(
                  PATH_LIST.assignmentSignContract(
                    assignment.type,
                    assignment.id
                  )
                );
              }
            }}
            style={{ cursor: "pointer", width: "auto" }}
          >
            {signed ? t("downloadPDF") : t("sign")}
          </p>
        )}
        <p style={{ width: "auto" }}>
          {moment(data.end_date).format("DD MMM YYYY")}
        </p>
      </div>
    </div>
  );
};

type PropsType =
  | {
      type: "repo";
      data: IRepoDeal;
    }
  | {
      type: "deposit-payment";
      data: IDepositPayment;
    };
const RepoTransactionField: FC<PropsType> = ({ type, data }) => {
  const { currencies } = useStore(dashboardStore);
  const { t } = useTranslation("app");

  const symbol = getCurrencySymbol(data.currency, currencies);

  if (type === "repo") {
    const asset = data.asset_id
      ? data?.asset?.isin || data?.asset?.symbol || null
      : null;

    return (
      <div className="repo-transaction-field">
        <div className="repo-transaction-field-header">
          {/* <p>{asset || ""}</p> */}
          <p>{t(`repoType_${data.repo_type}`)}</p>
        </div>
        <div className="repo-transaction-field-main">
          {[
            // { label: t("type"), text: t(`repoType_${data.repo_type}`) },
            { label: t("asset"), text: asset || "" },
            { label: t("quantity"), text: `${data.quantity_under_repo}` },
            { label: t("rate"), text: `${data.rate}%` },
            { label: t("currency"), text: data.currency },
            { label: t("amount"), text: `${data.received_amount} ${symbol}` },
          ].map((item, index) => (
            <RepoFieldMainComponent
              key={index}
              label={item.label}
              text={item.text}
            />
          ))}
        </div>

        <RepoFieldFooterComponent data={data} symbol={symbol} />
      </div>
    );
  } else {
    return (
      <div className="repo-transaction-field">
        <div className="repo-transaction-field-header">
          <p>{data.currency}</p>
          <div>
            <p>
              {t("annualYield")}{" "}
              <span
                style={{
                  color: data.paid ? "#27AE60" : "#f8009e",
                }}
              >
                {data.percent}%
              </span>
            </p>
          </div>
        </div>

        <div className="repo-transaction-field-main">
          {[
            { label: t("amount"), text: `${data.fix_amount} ${symbol}` },
            {
              label: t("totalProfit"),
              text: `${
                data.paid ? data.paid_percent : data.expected_profit
              } ${symbol}`,
            },
            ...(!data.paid
              ? [
                  {
                    label: t("currentProfit"),
                    text: `${data.current_profit ?? 0} ${symbol}`,
                  },
                ]
              : []),
          ].map((item, index) => (
            <RepoFieldMainComponent
              key={index}
              label={item.label}
              text={item.text}
            />
          ))}
        </div>

        <RepoFieldFooterComponent data={data} symbol={symbol} />
      </div>
    );
  }
};

export default RepoTransactionField;
